import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { JcUtils } from '../../utils/JcUtils';

export interface JcPopupProps {
    readonly open: boolean;
    readonly onClose: () => void;
    /** Renders edge-docked at the bottom of the viewport with a slide-up transition, instead of a centered modal — for a picker/list that reopens repeatedly (e.g. after every scoring tap), where a centered modal starts to feel heavy-handed. Pair with `JcDialog`'s `flushContent`. */
    readonly bottomSheet?: boolean;
    /** Classes for the modal panel, for a surface other than a `JcDialog` (e.g. `JcLightbox`). */
    readonly className?: string;
    /** Accessible name for the modal, when its content has no visible title. */
    readonly ariaLabel?: string;
    /** Typically a `JcDialog`. */
    readonly children: React.ReactNode;
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shows `children` (typically a `JcDialog`) in a modal overlay, for dialogs
 * opened from something other than their own trigger button (e.g. a
 * `JcActionMenu` item, or a plain state flag). Renders nothing while closed.
 *
 * `JcDialog` itself has no overlay, positioning, or accessibility behavior —
 * it's pure content. A modal `JcPopupButton` renders this component internally
 * to get the same behavior (scrim, Escape-to-close, focus trap, focus restore,
 * `aria-modal`) for a dialog with no trigger of its own. Never render a bare
 * `<JcDialog>` outside one of these two.
 */
export const JcPopup = ({ open, onClose, bottomSheet, className, ariaLabel, children }: JcPopupProps): React.ReactElement | null => {
    const contentRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }

        previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const content = contentRef.current;
        const focusable = content?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        focusable?.[0]?.focus();

        // Locks the page behind the modal, not just the dialog's own content —
        // without this, a wheel/touch scroll over the backdrop (the gutter
        // between the panel and the viewport edge) still scrolls the page.
        const previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key !== 'Tab' || content == null) {
                return;
            }
            const focusableElements = Array.from(content.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
            if (focusableElements.length === 0) {
                return;
            }
            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousBodyOverflow;
            previouslyFocused.current?.focus();
        };
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    return createPortal(
        <div
            className={JcUtils.classNameString(['jc-popup-overlay', bottomSheet && 'jc-popup-overlay-bottom-sheet'])}
            onClick={e => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                ref={contentRef}
                className={JcUtils.classNameString(['jc-popup-panel', className])}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
            >
                {children}
            </div>
        </div>,
        document.body,
    );
};
